export interface VehicleVisualIdentity {
  token: string;
  background: string;
  foreground: string;
  border: string;
}

// Deliberately ordered for maximum separation between the first operational cars.
// A workspace registry assigns each physical vehicle exactly one slot, so two cars
// can never collide just because their hashes happen to land on the same color.
const VEHICLE_PALETTE: VehicleVisualIdentity[] = [
  { token: "vehicle-blue", background: "#DBEAFE", foreground: "#1E3A8A", border: "#2563EB" },
  { token: "vehicle-red", background: "#FEE2E2", foreground: "#7F1D1D", border: "#DC2626" },
  { token: "vehicle-green", background: "#DCFCE7", foreground: "#14532D", border: "#16A34A" },
  { token: "vehicle-orange", background: "#FFEDD5", foreground: "#7C2D12", border: "#EA580C" },
  { token: "vehicle-purple", background: "#F3E8FF", foreground: "#581C87", border: "#9333EA" },
  { token: "vehicle-teal", background: "#CCFBF1", foreground: "#134E4A", border: "#0F766E" },
  { token: "vehicle-pink", background: "#FCE7F3", foreground: "#831843", border: "#DB2777" },
  { token: "vehicle-lime", background: "#ECFCCB", foreground: "#365314", border: "#65A30D" },
  { token: "vehicle-cyan", background: "#CFFAFE", foreground: "#164E63", border: "#0891B2" },
  { token: "vehicle-amber", background: "#FEF3C7", foreground: "#78350F", border: "#D97706" },
  { token: "vehicle-indigo", background: "#E0E7FF", foreground: "#312E81", border: "#4F46E5" },
  { token: "vehicle-rose", background: "#FFE4E6", foreground: "#881337", border: "#BE123C" },
  { token: "vehicle-emerald", background: "#D1FAE5", foreground: "#064E3B", border: "#059669" },
  { token: "vehicle-yellow", background: "#FEF9C3", foreground: "#713F12", border: "#CA8A04" },
  { token: "vehicle-violet", background: "#EDE9FE", foreground: "#4C1D95", border: "#7C3AED" },
  { token: "vehicle-sky", background: "#E0F2FE", foreground: "#0C4A6E", border: "#0284C7" },
  { token: "vehicle-fuchsia", background: "#FAE8FF", foreground: "#701A75", border: "#C026D3" },
  { token: "vehicle-olive", background: "#F7FEE7", foreground: "#3F6212", border: "#4D7C0F" },
  { token: "vehicle-coral", background: "#FFF1F2", foreground: "#9F1239", border: "#F43F5E" },
  { token: "vehicle-mint", background: "#ECFDF5", foreground: "#065F46", border: "#10B981" },
  { token: "vehicle-copper", background: "#FFF7ED", foreground: "#7C2D12", border: "#C2410C" },
  { token: "vehicle-grape", background: "#F5F3FF", foreground: "#5B21B6", border: "#6D28D9" },
  { token: "vehicle-aqua", background: "#ECFEFF", foreground: "#155E75", border: "#0E7490" },
  { token: "vehicle-magenta", background: "#FDF2F8", foreground: "#9D174D", border: "#BE185D" },
];

const fallbackAssignments = new Map<string, VehicleVisualIdentity>();
const fallbackPaletteOwners = new Map<number, string>();

function stableHash(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function generatedIdentity(index: number): VehicleVisualIdentity {
  const extra = index - VEHICLE_PALETTE.length;
  const hue = Math.round((extra * 137.508 + 23) % 360);
  return {
    token: `vehicle-generated-${index + 1}`,
    background: `hsl(${hue} 85% 92%)`,
    foreground: `hsl(${hue} 62% 24%)`,
    border: `hsl(${hue} 72% 43%)`,
  };
}

function identityAt(index: number) {
  return index < VEHICLE_PALETTE.length ? VEHICLE_PALETTE[index] : generatedIdentity(index);
}

export function createVehicleVisualRegistry(vehicleIds: Iterable<string>): Map<string, VehicleVisualIdentity> {
  const ids = [...new Set([...vehicleIds].map((value) => String(value || "").trim()).filter(Boolean))]
    .sort((left, right) => left.localeCompare(right, "en", { numeric: true, sensitivity: "base" }));
  return new Map(ids.map((vehicleId, index) => [vehicleId, identityAt(index)]));
}

function collisionFreeFallback(vehicleId: string): VehicleVisualIdentity {
  const existing = fallbackAssignments.get(vehicleId);
  if (existing) return existing;

  const start = stableHash(vehicleId) % VEHICLE_PALETTE.length;
  for (let offset = 0; offset < VEHICLE_PALETTE.length; offset += 1) {
    const index = (start + offset) % VEHICLE_PALETTE.length;
    if (!fallbackPaletteOwners.has(index)) {
      const identity = VEHICLE_PALETTE[index];
      fallbackPaletteOwners.set(index, vehicleId);
      fallbackAssignments.set(vehicleId, identity);
      return identity;
    }
  }

  const identity = generatedIdentity(VEHICLE_PALETTE.length + fallbackAssignments.size - VEHICLE_PALETTE.length);
  fallbackAssignments.set(vehicleId, identity);
  return identity;
}

export function vehicleVisualIdentity(
  vehicleId: string,
  registry?: ReadonlyMap<string, VehicleVisualIdentity>,
): VehicleVisualIdentity {
  const id = String(vehicleId || "").trim();
  if (id && registry?.has(id)) return registry.get(id)!;
  if (!id) return VEHICLE_PALETTE[0];
  // Legacy callers do not have a complete workspace registry. They still get a
  // collision-free assignment for the current app session instead of hash % N.
  return collisionFreeFallback(id);
}
